// The two halves of a code, mirrored from
// `backend/src/domain/codeComposition.ts`'s `decompose` / `compose`.
// That module is the backend's, which `app/src` does not import (only
// `@ap/shared`), so this is the app's own copy — the ONE place `app/src`
// interprets a code's shape. Every other site that used to
// slice the string by hand (`TrainingScreen.tsx`'s `beginRetire`, `data.ts`'s
// `loadDepartmentReach`) calls this instead (owner item 9, 2026-08-19), so
// the shape is asserted in one place rather than three.

/** `######-##-###`, anchored. Nothing else is a code. */
const CODE_ID = /^(\d{6})-(\d{2}-\d{3})$/
/** The leading segment, alone. */
const DEPARTMENT_ID = /^\d{6}$/
/** The trailing pair, alone. */
const ACCOUNT_ID = /^\d{2}-\d{3}$/

export interface CodeHalves {
  departmentId: string
  accountId: string
}

/** Splits a code. Returns null for anything not exactly `######-##-###`. */
export function decomposeCodeId(codeId: string | null | undefined): CodeHalves | null {
  if (typeof codeId !== 'string') return null
  const match = CODE_ID.exec(codeId.trim())
  if (match === null) return null
  const [, departmentId, accountId] = match
  return { departmentId: departmentId as string, accountId: accountId as string }
}

/**
 * Joins two halves into a code. Returns null unless BOTH are present
 * and well formed. Does NOT check that the result names a real code —
 * that is a fact about the chart, not about string shape.
 */
export function composeCodeId(
  departmentId: string | null | undefined,
  accountId: string | null | undefined,
): string | null {
  if (typeof departmentId !== 'string' || typeof accountId !== 'string') return null
  const department = departmentId.trim()
  const account = accountId.trim()
  if (!DEPARTMENT_ID.test(department) || !ACCOUNT_ID.test(account)) return null
  return `${department}-${account}`
}

/** True when the string is shaped like a department id. */
export function isDepartmentId(value: string | null | undefined): boolean {
  return typeof value === 'string' && DEPARTMENT_ID.test(value.trim())
}

/** True when the string is shaped like an account id. */
export function isAccountId(value: string | null | undefined): boolean {
  return typeof value === 'string' && ACCOUNT_ID.test(value.trim())
}

// ---------------------------------------------------------------------------
// Input masking — owner item 10(b), 2026-08-31
// ---------------------------------------------------------------------------
//
// "In the Code input boxes, can you automatically enforce the dash where
// applicable?" — WHERE APPLICABLE is load-bearing: an account is `NN-NNN`
// and gets the dash inserted after two digits; a department is six plain
// digits and gets NONE. Adding one there would be a new way to write a
// department id the database rejects.
//
// A MASK IS AN ASSIST, NEVER THE GATE. The regexes above are still what decides
// whether an id is well formed, and the dialog still refuses a malformed one:
// nothing here can be relied on to have run (a paste, an autofill and a
// programmatic set all reach the field, and the server never sees this file).
//
// Pure and caret-aware because that is the only way to test it. The three cases
// a naive `value.replace(...)` gets wrong, all covered in
// `app/mock/tests/code-id-mask.test.ts`:
//
//   * PASTE — `12-345`, `12345` and `1 2-3 4 5` are the same five digits, and
//     a paste into the middle re-lays the whole field rather than appending.
//   * BACKSPACE OVER THE DASH — deleting the separator alone would be undone
//     by re-inserting it, so the keystroke would visibly do nothing. Detected
//     by comparing digit sequences with the previous value: same digits, fewer
//     characters means only a separator went, and the digit to its LEFT goes
//     with it, which is what the user meant.
//   * A CARET IN THE MIDDLE — the caret is carried as a count of digits before
//     it, never as a string offset, so re-laying the field cannot make it jump.

/** A masked value and where the caret belongs in it. */
export interface MaskedId {
  value: string
  caret: number
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

function applyMask(
  next: string,
  caret: number,
  previous: string | undefined,
  maxDigits: number,
  /** How many digits precede the dash, or null for a mask with no separator. */
  dashAfter: number | null,
): MaskedId {
  const at = Math.max(0, Math.min(caret, next.length))
  let before = digitsOnly(next.slice(0, at)).length
  let digits = digitsOnly(next)

  // Backspace/delete that removed a separator and no digit: take the digit the
  // separator was standing next to, which is the one the user was aiming at.
  if (
    previous !== undefined &&
    next.length < previous.length &&
    digits === digitsOnly(previous) &&
    before > 0
  ) {
    digits = digits.slice(0, before - 1) + digits.slice(before)
    before -= 1
  }

  if (digits.length > maxDigits) digits = digits.slice(0, maxDigits)
  if (before > digits.length) before = digits.length

  const value =
    dashAfter === null || digits.length <= dashAfter
      ? digits
      : `${digits.slice(0, dashAfter)}-${digits.slice(dashAfter)}`

  // Walk to just past `before` digits, then past any separator sitting there —
  // so typing the second digit of `12` leaves the caret AFTER the dash and the
  // third digit lands where the user expects.
  let seen = 0
  let index = 0
  while (index < value.length && seen < before) {
    if (/\d/.test(value.charAt(index))) seen += 1
    index += 1
  }
  while (index < value.length && !/\d/.test(value.charAt(index))) index += 1
  return { value, caret: index }
}

/**
 * The Account half: five digits laid out `NN-NNN`, dash inserted.
 *
 * `previous` is the value the field held before this edit; pass it and a
 * backspace over the dash removes the digit to its left instead of being
 * silently undone. Omit it and the mask still works, just less kindly.
 */
export function maskAccountId(next: string, caret: number, previous?: string): MaskedId {
  return applyMask(next, caret, previous, 5, 2)
}

/**
 * The Department half: six digits, NO separator. Non-digits are
 * dropped and the field is capped, but nothing is inserted — `190110` is six
 * digits and `190-110` is not a department id.
 */
export function maskDepartmentId(next: string, caret: number, previous?: string): MaskedId {
  return applyMask(next, caret, previous, 6, null)
}

/**
 * The ONE department an account composes with today — or `null` when it
 * has none, has more than one, or already composes with `candidate`.
 *
 * This is the test behind the Add Reference dialog's "this adds a second
 * department" warning, and it lives here rather than inline in the dialog
 * because it was WRONG inline and nothing could see it: the dialog fired on
 * "does not already have this department" and then printed a sentence that
 * asserted a COUNT it had never checked, so pairing `67-506` (Uniforms, on
 * fifteen departments in the real chart) with a sixteenth announced that it
 * had one. Owner-reported, 2026-09-01.
 *
 * Retired codes do not count. A code whose id is malformed is skipped
 * rather than guessed at.
 */
export function soleDepartmentForAccount(
  chart: readonly { code_id: string; is_active?: boolean }[],
  accountId: string,
  candidateDepartmentId: string,
): string | null {
  const departments = new Set<string>()
  for (const code of chart) {
    if (code.is_active === false) continue
    const halves = decomposeCodeId(code.code_id)
    if (halves === null || halves.accountId !== accountId) continue
    departments.add(halves.departmentId)
    // Two is already "not exactly one" — no need to read the rest of a
    // 269-row chart to learn that.
    if (departments.size > 1) return null
  }
  if (departments.size !== 1) return null
  const [only] = [...departments]
  return only === candidateDepartmentId ? null : (only ?? null)
}

/** One row of an Account or Department picker: its ID, its name, and the
    controller's own alternative wordings for it. */
export interface HalfOption {
  id: string
  name: string
  searchTerms?: string | null
}

/**
 * Does a half match what was typed into its picker (owner, 2026-09-01: "make
 * those Account and Department dropdowns searchable by their NNNNNN and NN-NNN
 * IDs")?
 *
 * The References pickers hold their whole master in memory and filter HERE,
 * where the Rules pickers send `q` to the API and are filtered there. So this
 * deliberately mirrors the server's own three-field rule
 * (`backend/src/repo/pg/masters.ts`: `<name> ILIKE … OR <id> ILIKE … OR
 * search_terms ILIKE …`) rather than inventing a second search — the same
 * typing should find the same rows on both screens.
 *
 * ONE DELIBERATE ADDITION: the ID also matches with its separator ignored, so
 * `67506` finds `67-506`. An Account id is the only one of the three that
 * carries punctuation, and a person reading it off a table and typing it back
 * should not have to remember the dash. This makes the client strictly MORE
 * forgiving than the server, never less, so nothing findable on Rules is
 * unfindable here.
 *
 * THAT ADDITION IS FOR AN ID BEING TYPED, SO IT IGNORES A QUERY WITH A LETTER
 * IN IT (QA 2026-09-23, T1-05). It stripped every non-digit from whatever was
 * typed, so `QA T1` became `1` and offered every department whose ID holds a
 * 1 — thirteen of them. A query with a letter is a name search, and the name,
 * search-term and plain ID checks above already answer it.
 */
export function halfMatchesSearch(option: HalfOption, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  if (option.name.toLowerCase().includes(needle)) return true
  if ((option.searchTerms ?? '').toLowerCase().includes(needle)) return true
  if (option.id.toLowerCase().includes(needle)) return true
  if (/\p{L}/u.test(needle)) return false
  const digitsOnly = (value: string) => value.replace(/\D/g, '')
  const needleDigits = digitsOnly(needle)
  return needleDigits !== '' && digitsOnly(option.id).includes(needleDigits)
}
